import {
  formatNZD,
  removePricesAndCharacteristics,
} from '@sparknz/journeys-common';

import { GalleryProduct } from '../typings';

type ConstructIfpDescriptionParameters = {
  isBusiness: boolean;
  ifpSaving?: number;
  priceType?: GalleryProduct['priceType'];
} & Pick<
  GalleryProduct,
  | 'defaultPlan'
  | 'defaultIfpTerm'
  | 'downPaymentRequired'
  | 'minimumDownPaymentAmount'
  | 'isDeferrable'
>;

const getPlanName = (
  plan: GalleryProduct['defaultPlan'],
  isBusiness: boolean,
) => {
  const planPrice = isBusiness ? plan.basePriceExcludingTax : plan.basePrice;
  return `$${planPrice} ${removePricesAndCharacteristics(plan?.name ?? '')}`;
};

const generateIfpDescription = (
  constructIfpDescriptionParameters: ConstructIfpDescriptionParameters,
) => {
  // Don't show anything for SME until IFP fields are properly handled in backend
  const {
    defaultPlan,
    defaultIfpTerm,
    downPaymentRequired,
    minimumDownPaymentAmount,
    isDeferrable,
    ifpSaving,
    priceType,
    isBusiness,
  } = constructIfpDescriptionParameters;

  if (!isDeferrable) return '';

  const hasIFPSaving =
    ifpSaving && priceType?.toLocaleLowerCase() === 'recurring';

  // Default descriptions for customers without default plan
  if (!defaultIfpTerm) {
    return 'Interest free payments options are available to existing Spark customers on an eligible plan';
  }

  if (!defaultPlan) {
    return `Interest free over ${defaultIfpTerm} months available for customers with a Spark account`;
  }

  const planName = getPlanName(defaultPlan, isBusiness);
  const baseDescription = `Interest free over ${defaultIfpTerm} months on a ${planName}`;

  if (downPaymentRequired) {
    const downPaymentDescription = `with a minimum $${minimumDownPaymentAmount} deposit, subject to credit checks.`;
    return hasIFPSaving
      ? `over ${defaultIfpTerm} months interest free on a ${planName} ${downPaymentDescription}`
      : `${baseDescription}, with a $${minimumDownPaymentAmount} upfront minimum deposit, subject to credit checks.`;
  }

  return hasIFPSaving
    ? `over ${defaultIfpTerm} months interest free on a ${planName}, subject to credit checks.`
    : baseDescription;
};

export const constructIfpDescription = (
  constructIfpDescriptionParameters: ConstructIfpDescriptionParameters,
) => {
  const { priceType, ifpSaving } = constructIfpDescriptionParameters;
  const ifpDescription = generateIfpDescription(
    constructIfpDescriptionParameters,
  );
  const isInteger = ifpSaving % 1 === 0;
  const formattedSavingPrice = formatNZD(ifpSaving, isInteger ? 0 : 2);
  if (ifpSaving && priceType?.toLocaleLowerCase() === 'recurring') {
    return `Save ${formattedSavingPrice} ${ifpDescription} Offer applied to monthly price.`;
  }
  return ifpDescription;
};
